<script lang="ts">
  /**
   * A room's code, the biggest thing on its screen because it is the one
   * thing the screen is for: read out across a table, or passed on with Copy
   * (the bare code) and Share (the system's share sheet, or the link copied
   * on a desktop). A line under the buttons says what just happened
   * ("Copied"), politely. On the accent: the thing to act on.
   *
   * The code is set in `--ui-font-digits` at the display step and spaced, so
   * look-alikes need not be told apart at speed.
   *
   * `pending` (since 1.4.0): the code is not here yet. A calm line
   * (`pendingLabel`, default the shell's "Waiting for the code…") stands
   * where the code goes, at the same height so nothing jumps when it lands;
   * Copy and Share are disabled and no link is drawn. The block says it is
   * busy (`aria-busy`), and the code is announced politely when it comes.
   *
   * Test ids (`testids`, each with a default): `card` `ui-lobby-code-card`,
   * `code` `ui-lobby-code`, `copy` `ui-lobby-copy`, `share`
   * `ui-lobby-share`, `note` `ui-lobby-code-note`, `link` `ui-lobby-link`.
   * While pending, the placeholder carries the `code` test id and
   * `data-pending`.
   */
  import { shellText } from '../i18n.svelte.js';
  import type { LobbyCodeProps } from '../templates/playTypes.js';

  interface Props extends LobbyCodeProps {
    readonly testids?:
      | {
          readonly card?: string;
          readonly code?: string;
          readonly copy?: string;
          readonly share?: string;
          readonly note?: string;
          readonly link?: string;
        }
      | undefined;
  }
  const {
    value,
    label,
    link,
    copyLabel,
    shareLabel,
    oncopy,
    onshare,
    note = null,
    pending = false,
    pendingLabel,
    testids,
  }: Props = $props();
</script>

<section
  class="ui-lobby-code"
  class:ui-lobby-code--pending={pending}
  aria-busy={pending ? 'true' : undefined}
  data-testid={testids?.card ?? 'ui-lobby-code-card'}
>
  <p class="ui-lobby-code-label">{label}</p>
  <!-- Polite: the code is announced when it lands. -->
  <div class="ui-lobby-code-slot" aria-live="polite">
    {#if pending}
      <!-- The code's own line, so the block keeps its height; the words in it
           are the body step. -->
      <p
        class="ui-lobby-code-value"
        data-pending="true"
        data-testid={testids?.code ?? 'ui-lobby-code'}
      >
        <span class="ui-lobby-code-pending">{pendingLabel ?? shellText('lobbyCodePending')}</span>
      </p>
    {:else}
      <p
        class="ui-lobby-code-value"
        data-ui-font="digits"
        data-testid={testids?.code ?? 'ui-lobby-code'}
      >
        {value}
      </p>
    {/if}
  </div>
  <div class="ui-lobby-code-actions">
    <button
      type="button"
      class="ui-lobby-code-button"
      disabled={pending}
      data-testid={testids?.copy ?? 'ui-lobby-copy'}
      onclick={oncopy}
    >
      {copyLabel}
    </button>
    <button
      type="button"
      class="ui-lobby-code-button"
      disabled={pending}
      data-testid={testids?.share ?? 'ui-lobby-share'}
      onclick={onshare}
    >
      {shareLabel}
    </button>
  </div>
  {#if link !== undefined && !pending}
    <p class="ui-lobby-code-link" data-testid={testids?.link ?? 'ui-lobby-link'}>{link}</p>
  {/if}
  {#if note !== null}
    <p class="ui-lobby-code-note" role="status" data-testid={testids?.note ?? 'ui-lobby-code-note'}>
      {note}
    </p>
  {/if}
</section>

<style>
  .ui-lobby-code {
    margin: 0;
    padding: 1.25rem;
    border-radius: var(--ui-radius-lg);
    background: linear-gradient(155deg, var(--ui-accent), var(--ui-accent-deep));
    color: var(--ui-on-accent);
    font-family: var(--ui-font);
    text-align: center;
  }

  .ui-lobby-code-label {
    margin: 0;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    opacity: 0.85;
  }

  .ui-lobby-code-value {
    margin: 0.35rem 0 0.9rem;
    font: var(--ui-text-display);
    font-family: var(--ui-font-digits);
    font-weight: 700;
    /* Spaced, and the spacing's trailing pad pulled back so it stays centred. */
    letter-spacing: 0.28em;
    text-indent: 0.28em;
  }

  /* Waiting: plain words at the body step, calm, inside the code's own line
     (the paragraph keeps the code's line height, so nothing moves when the
     code lands). */
  .ui-lobby-code-pending {
    display: inline-block;
    font: var(--ui-text-body);
    font-weight: 700;
    letter-spacing: 0;
    text-indent: 0;
    vertical-align: middle;
  }

  .ui-lobby-code-actions {
    display: flex;
    gap: 0.6rem;
  }

  .ui-lobby-code-button {
    flex: 1;
    min-height: 2.75rem;
    padding: 0.7rem 1rem;
    border: 0;
    border-radius: var(--ui-radius-pill);
    background: var(--ui-on-accent);
    color: var(--ui-accent-deep);
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-lobby-code-button:disabled {
    opacity: 0.55;
  }

  .ui-lobby-code-button:focus-visible {
    outline: 3px solid var(--ui-on-accent);
    outline-offset: 2px;
  }

  .ui-lobby-code-link {
    margin: 0.6rem 0 0;
    font: var(--ui-text-caption);
    overflow-wrap: anywhere;
  }

  .ui-lobby-code-note {
    margin: 0.6rem 0 0;
    font: var(--ui-text-label);
    opacity: 0.9;
  }
</style>
